/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import { replace } from 'lodash';
import { useMemo, useCallback, ChangeEvent, MouseEventHandler } from 'react';
import { styled } from 'styled-components';
import { RadioGroup as MotiliRadioGroup } from '@motili/ui';
import { DisplayType } from 'common/types';

type RadioGroupProps = {
    name: string;
    label: string;
    value?: string | number | boolean;
    options: DisplayType<number | string | boolean>[];
    onChange: (value: string) => void;
    onClick: MouseEventHandler<HTMLInputElement>;
    required?: boolean;
    dataTestId?: string;
    className?: string;
    disabled?: boolean;
};
export default function RadioGroup(props: RadioGroupProps) {
    const {
        name,
        label,
        value,
        options,
        onChange,
        required,
        dataTestId,
        className,
        onClick,
        disabled,
    } = props;
    const chkOptions = useMemo(() => {
        return options.map(option => ({
            checked: value === option.id,
            value: name ? `${name}-${option.id}` : `${option.id}`,
            label: option.display,
        }));
    }, [options, value]);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            onChange(replace(e.target.value, `${name}-`, ''));
        },
        [onChange, name]
    );
    return (
        <StyledRadioGroup className={className}>
            <MotiliRadioGroup
                className=''
                dataTestId={dataTestId}
                name={name}
                label={label}
                onChange={handleChange}
                options={chkOptions}
                required={required}
                onClick={onClick}
                disabled={disabled}
            />
        </StyledRadioGroup>
    );
}

const StyledRadioGroup = styled.div`
    div.wrapper {
        & > div {
            border-color: #cccccc;
            margin-right: 10px;
        }
        label {
            font-weight: 400;
            font-size: 14px;
            line-height: 19px;
            color: #333;
        }
    }
`;
